import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Send, AlertTriangle, ShieldAlert, FileText, ClipboardCheck, Clock,
  ChevronRight, X, RefreshCw, CheckCircle2, XCircle, Info, ArrowRight,
  ListChecks, Eye, CornerDownRight, Zap, ThumbsUp, ThumbsDown,
  Globe, Package, RotateCw, Radio, Hash
} from 'lucide-react';
import {
  PredictiveLabel, RiskBadge, ConfidenceText, FeedbackButtons,
  StatusChip, SlidePanel, Timeline, EnvironmentBadge, TransportStatusChip,
  ExternalSourceBadge, RawResponseViewer, DenialStateBadge,
  fmt, _typeLabel, _fmtK, _buildFocusSummary
} from './RcmShared';

function IntelligenceTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/rcm/intelligence/summary'); setData(r.data); }
    catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="p-4 text-sm text-muted-foreground">Generating intelligence...</div>;
  if (!data) return null;

  return (
    <div className="space-y-4" data-testid="intelligence-tab">
      {/* Read-only banner */}
      <div className="bg-violet-50 border border-violet-200 rounded-lg px-4 py-2 flex items-center gap-2 text-xs">
        <Zap className="h-3.5 w-3.5 text-violet-600 shrink-0" />
        <span className="text-violet-800">Strategic Intelligence — read-only insights generated from RCM data. No actions are executed from this view.</span>
      </div>

      {/* Revenue Risks */}
      {data.revenue_risks?.length > 0 && (
        <Card className="border-red-200" data-testid="revenue-risks">
          <CardHeader className="pb-1 pt-3 px-3"><CardTitle className="text-xs text-red-700">Revenue Risks</CardTitle></CardHeader>
          <CardContent className="px-3 pb-3 space-y-2">
            {data.revenue_risks.map((r, i) => (
              <div key={i} className={`border rounded p-2.5 text-xs ${r.severity === 'high' ? 'border-red-200 bg-red-50/30' : 'border-amber-200 bg-amber-50/30'}`} data-testid={`risk-${r.type}`}>
                <div className="flex justify-between items-center">
                  <span className="font-medium">{r.label}</span>
                  <div className="flex items-center gap-2">
                    {r.amount > 0 && <span className="font-bold text-red-600">{fmt(r.amount)}</span>}
                    <Badge variant={r.severity === 'high' ? 'destructive' : 'outline'} className="text-[9px]">{r.count} items</Badge>
                  </div>
                </div>
                <p className="text-muted-foreground mt-1">{r.recommendation}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Payer Metrics + Underpayments side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Payer Metrics */}
        <Card data-testid="payer-metrics">
          <CardHeader className="pb-1 pt-3 px-3"><CardTitle className="text-xs">Payer Behavior</CardTitle></CardHeader>
          <CardContent className="px-3 pb-3">
            <table className="w-full text-xs">
              <thead><tr className="border-b text-left">
                <th className="py-1.5 font-medium text-muted-foreground">Payer</th>
                <th className="py-1.5 font-medium text-muted-foreground text-right">Denial Rate</th>
                <th className="py-1.5 font-medium text-muted-foreground text-right">Denials</th>
                <th className="py-1.5 font-medium text-muted-foreground text-right">$ Impact</th>
                <th className="py-1.5 font-medium text-muted-foreground">Top Codes</th>
              </tr></thead>
              <tbody>{data.top_payer_metrics?.map((p, i) => (
                <tr key={i} className="border-b">
                  <td className="py-1.5 font-medium">{p.payer_name}</td>
                  <td className={`py-1.5 text-right font-medium ${p.denial_rate > 20 ? 'text-red-600' : p.denial_rate > 10 ? 'text-amber-600' : 'text-emerald-600'}`}>{p.denial_rate}%</td>
                  <td className="py-1.5 text-right">{p.total_denials}</td>
                  <td className="py-1.5 text-right text-red-600">{fmt(p.total_denial_amount)}</td>
                  <td className="py-1.5">{p.top_denial_codes?.slice(0,3).map((c,j) => <Badge key={j} variant="outline" className="text-[8px] mr-0.5 py-0">{c.code}</Badge>)}</td>
                </tr>
              ))}</tbody>
            </table>
          </CardContent>
        </Card>

        {/* Underpayments */}
        <Card data-testid="underpayments">
          <CardHeader className="pb-1 pt-3 px-3 flex flex-row justify-between items-center">
            <CardTitle className="text-xs">Underpayment Flags</CardTitle>
            <Badge variant="destructive" className="text-[9px]">{data.underpayment_summary?.total_flags} flags · {fmt(data.underpayment_summary?.total_underpaid)}</Badge>
          </CardHeader>
          <CardContent className="px-3 pb-3">
            <table className="w-full text-xs">
              <thead><tr className="border-b text-left">
                <th className="py-1 font-medium text-muted-foreground">Claim</th>
                <th className="py-1 font-medium text-muted-foreground text-right">Expected</th>
                <th className="py-1 font-medium text-muted-foreground text-right">Paid</th>
                <th className="py-1 font-medium text-muted-foreground text-right">Variance</th>
                <th className="py-1 font-medium text-muted-foreground text-right">%</th>
              </tr></thead>
              <tbody>{data.underpayment_summary?.top_flags?.map((f, i) => (
                <tr key={i} className="border-b">
                  <td className="py-1 font-mono text-[10px]">{f.claim_id?.slice(0,8)}...</td>
                  <td className="py-1 text-right text-muted-foreground">{fmt(f.expected_allowed)}</td>
                  <td className="py-1 text-right">{fmt(f.actual_paid)}</td>
                  <td className="py-1 text-right text-red-600 font-medium">{fmt(f.variance)}</td>
                  <td className="py-1 text-right text-red-600">{f.variance_percent}%</td>
                </tr>
              ))}</tbody>
            </table>
          </CardContent>
        </Card>
      </div>

      {/* Denial Patterns + Execution Outcomes side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Denial Patterns */}
        <Card data-testid="denial-patterns">
          <CardHeader className="pb-1 pt-3 px-3"><CardTitle className="text-xs">Denial Patterns</CardTitle></CardHeader>
          <CardContent className="px-3 pb-3 space-y-1.5">
            {data.denial_patterns?.map((p, i) => (
              <div key={i} className="flex items-center justify-between text-xs border-b pb-1 last:border-0">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-[9px]">{p.label}</Badge>
                  <span className="text-muted-foreground">{p.count} denials</span>
                </div>
                <span className="font-medium text-red-600">{fmt(p.total_amount)}</span>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Execution Outcomes */}
        <Card data-testid="execution-outcomes">
          <CardHeader className="pb-1 pt-3 px-3"><CardTitle className="text-xs">Execution Success Rates</CardTitle></CardHeader>
          <CardContent className="px-3 pb-3 space-y-2">
            {data.execution_outcomes?.map((o, i) => (
              <div key={i} className="text-xs">
                <div className="flex justify-between items-center mb-1">
                  <span className="font-medium capitalize">{o.action_type}</span>
                  <span className={`font-bold ${o.success_rate >= 70 ? 'text-emerald-600' : o.success_rate >= 40 ? 'text-amber-600' : 'text-red-600'}`}>{o.success_rate}%</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full ${o.success_rate >= 70 ? 'bg-emerald-500' : o.success_rate >= 40 ? 'bg-amber-500' : 'bg-red-500'}`} style={{width: `${Math.max(4, o.success_rate)}%`}} />
                </div>
                <div className="flex gap-2 text-[10px] text-muted-foreground mt-0.5">
                  <span>{o.completed} completed</span>
                  <span>{o.failed} failed</span>
                  <span>{o.pending} pending</span>
                </div>
              </div>
            ))}
            {(!data.execution_outcomes || data.execution_outcomes.length === 0) && (
              <p className="text-xs text-muted-foreground text-center py-2">No execution data yet</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════
// EXECUTION QUEUE TAB (Phase 6A.5)
// ═══════════════════════════════════════

export { IntelligenceTab };
